import { useNavigate } from "react-router-dom";
import { TextField, Button, IconButton, InputAdornment } from "@mui/material";
import { VisibilityOff, Visibility } from "@mui/icons-material";
import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { usePostChangePasswordMutation } from "../../service/auth";
import { showError, showToast } from "../../constants/toasts";
import { isValidInput } from "../../utils/validations";

const ChangePassword = () => {
  const navigate = useNavigate();
  const [changePassword] = usePostChangePasswordMutation();

  // Password visibility states
  const [show, setShow] = React.useState({
    old: false,
    new: false,
    confirm: false,
  });

  const toggleShow = (field: "old" | "new" | "confirm") => {
    setShow((prev) => ({ ...prev, [field]: !prev[field] }));
  };
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };
  const handleMouseDownPassword1 = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };
  const handleMouseDownPassword2 = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  // Formik + Yup
  const formik = useFormik({
    initialValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      oldPassword: Yup.string()
        .min(8, "Old Password must be at least 8 characters")
        .required("Old Password is required"),
      newPassword: Yup.string()
        .required("New Password is required")
        .min(8, "Password must be at least 8 characters")
        .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
        .matches(/[a-z]/, "Password must contain at least one lowercase letter")
        .matches(/[0-9]/, "Password must contain at least one number")
        .matches(
          /[@$!%*?&^#()\-_=+{}[\]|;:'",.<>/?]/,
          "Password must contain at least one special character"
        )
        .notOneOf(
          [Yup.ref("oldPassword")],
          "New password cannot be same as old password"
        ),

      confirmPassword: Yup.string()
        .required("Confirm password is required")
        .oneOf([Yup.ref("newPassword")], "Passwords do not match"),
    }),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        setSubmitting(true);

        const body = {
          oldPassword: values.oldPassword,
          newPassword: values.newPassword,
          // isResetPassword: values.oldPassword,
        };
        const response = await changePassword(body).unwrap();
        if (response?.code === 200) {
          showToast("Password changed successfully");
          navigate("/dashboard");
        }
        //  Fake API simulation (use real API later)
        // await new Promise((resolve) => setTimeout(resolve, 1200));

        // console.log("Password Updated Successfully", values);

        // alert(" Password changed successfully!");

        // resetForm();
      } catch (err: any) {
        console.error("Error changing password:", err);
        showError(err?.data?.message || " Failed to change password!");
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <>
      <div className="main_title">
        <h1>Change Password</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/manage-profile")}>
            Manage Profile
          </button>{" "}
          - Change Password
        </p>
      </div>

      <div className="cards">
        <div className="form">
          <form onSubmit={formik.handleSubmit}>
            <div className="gap_p">
              <div className="control_group w_33">
                <label htmlFor="oldPassword">Old Password</label>
                <TextField
                  id="oldPassword"
                  name="oldPassword"
                  placeholder="Enter Old Password"
                  hiddenLabel
                  fullWidth
                  type={show.old ? "text" : "password"}
                  value={formik.values.oldPassword}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (isValidInput(val) && val?.length < 40)
                      formik.handleChange(e);
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.oldPassword &&
                    Boolean(formik.errors.oldPassword)
                  }
                  helperText={
                    formik.touched.oldPassword && formik.errors.oldPassword
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" className="eye_btn">
                        <IconButton
                          aria-label={show.old ? "text" : "password"}
                          onClick={() => toggleShow("old")}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {show.old ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
              <div className="control_group w_33">
                <label htmlFor="newPassword">New Password</label>
                <TextField
                  id="newPassword"
                  name="newPassword"
                  placeholder="Enter New Password"
                  hiddenLabel
                  fullWidth
                  type={show.new ? "text" : "password"}
                  value={formik.values.newPassword}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (isValidInput(val) && val?.length < 40)
                      formik.handleChange(e);
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.newPassword &&
                    Boolean(formik.errors.newPassword)
                  }
                  helperText={
                    formik.touched.newPassword && formik.errors.newPassword
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" className="eye_btn">
                        <IconButton
                          aria-label={
                            show.new ? "Hide password" : "Show password"
                          }
                          onClick={() => toggleShow("new")}
                          onMouseDown={handleMouseDownPassword1}
                          edge="end"
                        >
                          {show.new ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
              <div className="control_group w_33">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <TextField
                  id="confirmPassword"
                  name="confirmPassword"
                  placeholder="Enter Confirm New Password"
                  hiddenLabel
                  fullWidth
                  type={show.confirm ? "text" : "password"}
                  value={formik.values.confirmPassword}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (isValidInput(val) && val?.length < 40)
                      formik.handleChange(e);
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.confirmPassword &&
                    Boolean(formik.errors.confirmPassword)
                  }
                  helperText={
                    formik.touched.confirmPassword &&
                    formik.errors.confirmPassword
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" className="eye_btn">
                        <IconButton
                          aria-label={
                            show.confirm ? "Hide password" : "Show password"
                          }
                          onClick={() => toggleShow("confirm")}
                          onMouseDown={handleMouseDownPassword2}
                          edge="end"
                        >
                          {show.confirm ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
            </div>
            <div className="form_btn">
              <Button type="submit" className="btn btn_primary">
                Save
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default ChangePassword;
